import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
} from "react";
import { EditorState, convertToRaw, convertFromRaw } from "draft-js";
import { questionEditorService } from "../../service/assessSections/questionEditorService";
import { passageService } from "../../service/question/passageService";
import { passageTypeService } from "../../service/question/passageTypeService";
import { mcqService } from "../../service/question/mcqService";
import { assessmentQuestionService } from "../../service/question/assessmentQuestionService";
import { assessmentService } from "../../service/assessment/assessmentService";
import DataHandler from "../../handlers/DataHandler";
import { descriptiveService } from "../../service/question/descriptiveService";
import { submissionService } from "../../service/question/submissionService";
import { AzureStorage } from "../../helper/azureStorage";
import config from "../../helper/config";
import { libraryService } from "../../service/question/libraryService";
import { questionTypeConstants } from "../../helper/constants";
import { updateQuestionById } from "../../service/assessSections/assessSectionsService/assessSectionsService";

const QuestionEditorContext = createContext(null);

const initialForm = {
  selectedTitle: "",
  selectedAssessmentTitle: "",
  selectedType: null,
  selectedAnswerType: null,
  selectedCategory: null,
  selectedDifficulty: null,
  selectedGrade: null,
  selectedSubject: null,
  selectedLanguage: null,
  selectedTerm: null,
  selectedChapter: null,
  selectedYear: null,
  selectedSource: null,
  selectedAuthor: null,
  selectedPrivilege: null,
  selectedTag: "",

  selectedPassageType: null,
  selectedPassage: null,
  selectedIsPassage: false,

  guidanceEditorState: EditorState.createEmpty(),
  contentEditorState: EditorState.createEmpty(),
  descriptiveAnswerEditorState: EditorState.createEmpty(),
  subQuestionEditorStates: [EditorState.createEmpty()],
  subQuestionAnswerEditorStates: [EditorState.createEmpty()],
  loadedSubQuestions: [],
  removedSubQuestionIds: [],

  choicesEditorState: [EditorState.createEmpty()],
  selectedChoices: [0],

  submissionFiles: [],
  submissionAnswerKey: "",
  submissionAnswerContent: [],
  submissionUploaded: true,
  submissionClearFile: false,

  selectedAssessment: null,
  selectedAssessmentId: null,
  selectedAssessmentTitle: "",
  selectedPart: null,
  selectedSection: null,
  assessmentParts: [],
  assessmentSections: [],

};

const emptyMeta = {
  categories: [],
  types: [],
  answer_types: [],
  difficulty_types: [],
  grades: [],
  subjects: [],
  languages: [],
  terms: [],
  chapters: [],
  years: [],
  sources: [],
  authors: [],
  privileges: [],
  passages: [],
  passage_types: [],
  assessments: [],
};

function normalizeMetadata(result) {
  const data = result?.data || result || {};

  return {
    categories: data.categories || [],
    types: data.types || [],
    answer_types: data.answer_types || [],
    difficulty_types: data.difficulty_types || [],
    grades: data.grades || [],
    subjects: data.subjects || [],
    languages: data.languages || [],
    terms: data.terms || [],
    chapters: data.chapters || [],
    years: data.years || [],
    sources: data.sources || [],
    authors: data.authors || [],
    privileges: data.privileges || [],
    passages: data.passages || [],
    passage_types: data.passage_types || [],
    assessments: data.assessments || [],
  };
}

function normalizeAssessmentParts(assessmentDetails) {
  return (assessmentDetails?.parts || [])
    .slice()
    .sort((a, b) => Number(a.display_order || 0) - Number(b.display_order || 0));
}
function normalizeSelectValue(value) {
  if (value === "" || value === undefined || value === "Nothing selected") {
    return null;
  }

  return value;
}

function unwrapContent(value) {
  if (!value) return null;

  if (value.data) return value.data;
  if (value.content) return value.content;
  if (value.answer) return value.answer;
  if (value.guidence) return value.guidence;

  return value;
}

function parseRawList(content) {
  try {
    const unwrapped = unwrapContent(content);
    if (!unwrapped) return [];

    const parsed =
      typeof unwrapped === "string" ? JSON.parse(unwrapped) : unwrapped;

    if (Array.isArray(parsed)) return parsed;
    return [parsed];
  } catch (error) {
    console.error("Failed to parse Draft content:", content, error);
    return [];
  }
}

function rawToEditorState(raw) {
  try {
    if (!raw || !raw.blocks) return EditorState.createEmpty();

    return EditorState.createWithContent(convertFromRaw(raw));
  } catch (error) {
    console.error("Failed to convert raw to editor state:", raw, error);
    return EditorState.createEmpty();
  }
}

function parseSubmissionAnswer(answerContent) {
  try {
    if (!answerContent) return [];

    const parsed =
      typeof answerContent === "string"
        ? JSON.parse(answerContent)
        : answerContent;

    if (Array.isArray(parsed)) return parsed.filter((item) => item !== "[]");

    return [];
  } catch {
    return String(answerContent || "")
      .replace(/^\[(.+)\]$/, "$1")
      .replace(/"/g, "")
      .split(",")
      .filter(Boolean);
  }
}

function toExistingUploadFiles(fileNames = []) {
  return fileNames.map((name) => ({
    name,
    existing: true,
  }));
}

function parseSubQuestionIds(value) {
  if (!value) return [];

  if (Array.isArray(value)) {
    return value.filter(Boolean).map(String);
  }

  return String(value)
    .split(",")
    .map((id) => id.trim())
    .filter(Boolean);
}

function buildChoiceMarkDistribution(currentMarks = [], index) {
  const nextSelections = currentMarks.map((mark) => Number(mark) > 0);
  nextSelections[index] = !nextSelections[index];

  if (!nextSelections.some(Boolean)) {
    return currentMarks.map(() => 0);
  }

  const selectedIndexes = nextSelections.reduce((acc, isSelected, currentIndex) => {
    if (isSelected) acc.push(currentIndex);
    return acc;
  }, []);

  const baseMark = Math.floor(100 / selectedIndexes.length);
  let remainder = 100 - baseMark * selectedIndexes.length;

  return currentMarks.map((_, currentIndex) => {
    if (!nextSelections[currentIndex]) return 0;

    const nextMark = baseMark + (remainder > 0 ? 1 : 0);
    if (remainder > 0) remainder -= 1;
    return nextMark;
  });
}

export function QuestionEditorProvider({
  children,
  includeAssessments = false,
  editQuestion: editQuestionProp = null,
  editQuestionId = null,
}) {
  const [form, setForm] = useState(initialForm);
  const [meta, setMeta] = useState(emptyMeta);
  const [metaLoading, setMetaLoading] = useState(false);
  const [metaError, setMetaError] = useState("");
  const [saving, setSaving] = useState(false);
  const [selectedPassageDetails, setSelectedPassageDetails] = useState(null);
  const [selectedPassageLoading, setSelectedPassageLoading] = useState(false);
  const [selectedPassageError, setSelectedPassageError] = useState("");
  const [editQuestion, setEditQuestion] = useState(null);
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState("");

  useEffect(() => {
    let mounted = true;

    async function loadMetadata() {
      try {
        setMetaLoading(true);
        setMetaError("");

        const result = await questionEditorService.getQuestionMetadata({
          includeAssessments,
          forceRefresh: includeAssessments,
        });

        if (!mounted) return;

        const normalizedMeta = normalizeMetadata(result);

        // const [passageTypesResult, passagesResult] = await Promise.all([
        //   passageTypeService.getPassageTypes(),
        //   passageService.getPassageTitle(),
        // ]);

        // normalizedMeta.passage_types =
        //   passageTypesResult?.data?.passage_types || [];

        // normalizedMeta.passages = passagesResult?.data?.passages || [];

        setMeta(normalizedMeta);

        setForm((prev) => ({
          ...prev,
          selectedType: normalizedMeta.types[0]?.type_id || null,
          selectedAnswerType:
            normalizedMeta.answer_types[0]?.answer_type_id || null,
          selectedPrivilege: normalizedMeta.privileges[0]?.privilege_id || null,
        }));
      } catch (error) {
        console.error("getQuestionMetadata failed:", error);
        if (!mounted) return;
        setMetaError(error?.message || "Failed to load question metadata");
      } finally {
        if (mounted) {
          setMetaLoading(false);
        }
      }
    }

    loadMetadata();

    return () => {
      mounted = false;
    };
  }, [includeAssessments]);


  // takes a selected assessment and auto-fills other metadata in create question with assessment
  const createQuestionWithAssessment = useCallback(async (assessment) => {
    const assessmentId = assessment?.assessment_id || null;

    setForm((prev) => ({
      ...prev,
      selectedAssessment: assessment,
      selectedAssessmentId: assessmentId,
      selectedAssessmentTitle: assessment?.title || "",

      selectedTitle: "",

      selectedGrade: assessment?.grade_id || null,
      selectedSubject: assessment?.subject_id || null,
      selectedLanguage: assessment?.language_id || null,
      selectedTerm: assessment?.term_id || null,
      selectedCategory: assessment?.exam_category_id || null,
      selectedDifficulty: assessment?.difficulty_id || null,
      selectedChapter: assessment?.chapter_id || null,
      selectedYear: assessment?.year_id || null,
      selectedSource: assessment?.source_id || null,
      selectedAuthor: assessment?.author_id || null,
      selectedPrivilege: assessment?.privilege_id || null,

      selectedPart: null,
      selectedSection: null,
      assessmentParts: [],
      assessmentSections: [],
    }));

    // if (!assessmentId) return;

    // const result = await assessmentService.getAssessmentById(assessmentId);
    // const assessmentDetails = result?.data?.assessmentDetails;

    // const parts = normalizeAssessmentParts(assessmentDetails);

    // setForm((prev) => ({
    //   ...prev,
    //   assessmentParts: parts,
    //   assessmentSections: [],
    //   selectedPart: null,
    //   selectedSection: null,
    // }));
  }, []);

  const updateField = useCallback((name, value) => {
    setForm((prev) => ({
      ...prev,
      [name]: normalizeSelectValue(value),
    }));
  }, []);

  const addOption = useCallback(() => {
    setForm((prev) => ({
      ...prev,
      choicesEditorState: [
        ...prev.choicesEditorState,
        EditorState.createEmpty(),
      ],
      selectedChoices: [...prev.selectedChoices, 0],
    }));
  }, []);

  const removeOption = useCallback((index) => {
    setForm((prev) => {
      if (prev.choicesEditorState.length <= 1) return prev;

      return {
        ...prev,
        choicesEditorState: prev.choicesEditorState.filter(
          (_, i) => i !== index
        ),
        selectedChoices: prev.selectedChoices.filter((_, i) => i !== index),
      };
    });
  }, []);

  const updateChoiceEditor = useCallback((index, editorState) => {
    setForm((prev) => {
      const nextChoices = [...prev.choicesEditorState];
      nextChoices[index] = editorState;

      return {
        ...prev,
        choicesEditorState: nextChoices,
      };
    });
  }, []);

  const updateChoiceMarks = useCallback((index, value) => {
    setForm((prev) => {
      return {
        ...prev,
        selectedChoices: buildChoiceMarkDistribution(prev.selectedChoices, index),
      };
    });
  }, []);

  const resetForm = useCallback(() => {
    setForm((prev) => ({
      ...initialForm,
      selectedType: prev.selectedType,
      selectedAnswerType: prev.selectedAnswerType,
      selectedPrivilege: prev.selectedPrivilege,
    }));
  }, []);

  const saveMCQQuestion = useCallback(async () => {
    const questionKey = `${Date.now()}`;

    const answer = form.selectedChoices.toString();

    const questionContent = [
      convertToRaw(form.contentEditorState.getCurrentContent()),
      ...form.choicesEditorState.map((editorState) =>
        convertToRaw(editorState.getCurrentContent())
      ),
    ];

    const question = {
      title: form.selectedTitle,
      qType: form.selectedType,
      answer_type_id: form.selectedAnswerType,
      categoryId: form.selectedCategory,
      questionKey,
      questionContent,
      createdBy: DataHandler.getFromSession("user_id"),
      answer,

      difficulty_id: form.selectedDifficulty || null,
      language_id: form.selectedLanguage || null,
      source_id: form.selectedSource || null,
      subject_id: form.selectedSubject || null,
      chapter_id: form.selectedChapter || null,
      grade_id: form.selectedGrade ? parseInt(form.selectedGrade, 10) : null,
      guidence: [convertToRaw(form.guidanceEditorState.getCurrentContent())],

      author_id: form.selectedAuthor || null,
      year_id: form.selectedYear || null,
      privilege_id: form.selectedPrivilege || null,
      term_id: form.selectedTerm || null,
      tags: form.selectedTag,

      is_passage: form.selectedIsPassage,
      passage_id: form.selectedIsPassage ? form.selectedPassage : null,

      assessment_id: form.selectedAssessmentId || null,
      //section_id: form.selectedSection ? parseInt(form.selectedSection, 10) : null,
    };

    if (form.selectedAssessmentId) {
      question.section_id = form.selectedSection
        ? parseInt(form.selectedSection, 10)
        : null;

    }

    setSaving(true);

    try {
      const result = form.selectedAssessmentId
        ? await assessmentQuestionService.saveMCQQuestion(question)
        : await mcqService.saveQuestion(question);

      if (!result?.success) {
        throw new Error(result?.message || "Failed to create MCQ question");
      }

      // resetForm();

      return result;
    } finally {
      setSaving(false);
    }
  }, [form, resetForm]);

  const saveDescriptiveQuestion = useCallback(async () => {
    const questionKey = `${Date.now()}`;
    const answerKey = `${Date.now()}`;

    const question = {
      title: form.selectedTitle,
      qType: form.selectedType,
      answer_type_id: null,
      categoryId: form.selectedCategory || null,
      questionKey,
      questionContent: [
        convertToRaw(form.contentEditorState.getCurrentContent()),
      ],
      createdBy: DataHandler.getFromSession("user_id"),

      answerKey,
      answerContent: [
        convertToRaw(form.descriptiveAnswerEditorState.getCurrentContent()),
      ],

      difficulty_id: form.selectedDifficulty || null,
      language_id: form.selectedLanguage || null,
      source_id: form.selectedSource || null,
      subject_id: form.selectedSubject || null,
      chapter_id: form.selectedChapter || null,
      grade_id: form.selectedGrade ? parseInt(form.selectedGrade, 10) : null,
      guidence: [convertToRaw(form.guidanceEditorState.getCurrentContent())],

      author_id: form.selectedAuthor || null,
      year_id: form.selectedYear || null,
      privilege_id: form.selectedPrivilege || null,
      term_id: form.selectedTerm || null,
      tags: form.selectedTag,

      is_passage: form.selectedIsPassage,
      passage_id: form.selectedIsPassage ? form.selectedPassage : null,

      assessment_id: form.selectedAssessmentId || null,
    };

    if (form.selectedAssessmentId) {
      question.section_id = form.selectedSection
        ? parseInt(form.selectedSection, 10)
        : null;
    }

    setSaving(true);

    try {
      const result = form.selectedAssessmentId
        ? await assessmentQuestionService.saveQuestion(question)
        : await descriptiveService.saveQuestion(question);

      if (!result?.success) {
        throw new Error(result?.message || "Failed to create Descriptive question");
      }

      return result;
    } finally {
      setSaving(false);
    }
  }, [form, resetForm]);

  const loadSelectedPassage = useCallback(async (passageId) => {
    if (!passageId) {
      setSelectedPassageDetails(null);
      setSelectedPassageError("");
      return;
    }

    try {
      setSelectedPassageLoading(true);
      setSelectedPassageError("");

      const result = await passageService.getPassageById(passageId);

      if (!result?.success) {
        throw new Error(result?.message || "Failed to load passage");
      }

      setSelectedPassageDetails(result.data?.passage || null);
    } catch (error) {
      setSelectedPassageError(error?.message || "Failed to load passage");
      setSelectedPassageDetails(null);
    } finally {
      setSelectedPassageLoading(false);
    }
  }, []);

  const loadQuestionForEdit = useCallback(
    async (question) => {
      if (!question) return;

      try {
        setEditLoading(true);
        setEditError("");
        setEditQuestion(question);

        const [questionContent, guidanceContent, answerContent] =
          await Promise.all([
            libraryService.getQuestionContent(question),
            libraryService.getQuestionGuidanceContent(question),
            libraryService.getAnswerContent(question),
          ]);

        const questionRawList = parseRawList(questionContent);
        const guidanceRawList = parseRawList(
          guidanceContent || question.guidence || question.guidance
        );
        const answerRawList = parseRawList(answerContent || question.answer);

        const isMCQ = question.qtype === questionTypeConstants.MCQ.id;
        const isDescriptive =
          question.qtype === questionTypeConstants.DESCRIPTIVE.id;
        const isSubQuestion =
          question.qtype === questionTypeConstants.SUBQUESTION.id;
        const isSubmission =
          question.qtype === questionTypeConstants.SUBMISSION.id;

        let loadedSubQuestionEditorStates = [EditorState.createEmpty()];
        let loadedSubQuestionAnswerEditorStates = [EditorState.createEmpty()];
        let loadedSubQuestionsForEdit = [];

        if (isSubQuestion) {
          const subQuestionIds = parseSubQuestionIds(question.sub_questions);

          if (subQuestionIds.length > 0) {
            const loadedSubQuestions = await Promise.all(
              subQuestionIds.map(async (subQuestionId) => {
                const result = await questionEditorService.getQuestionEditDetail(
                  subQuestionId
                );

                if (!result?.success || !result.data?.question) {
                  return null;
                }

                const subQuestion = result.data.question;

                const [subQuestionContent, subQuestionAnswerContent] =
                  await Promise.all([
                    libraryService.getQuestionContent(subQuestion),
                    libraryService.getAnswerContent(subQuestion),
                  ]);

                const subQuestionRawList = parseRawList(subQuestionContent);
                const subQuestionAnswerRawList = parseRawList(
                  subQuestionAnswerContent || subQuestion.answer
                );

                return {
                  question: subQuestion,
                  questionEditorState: rawToEditorState(subQuestionRawList[0]),
                  answerEditorState: rawToEditorState(subQuestionAnswerRawList[0]),
                };
              })
            );

            const validSubQuestions = loadedSubQuestions.filter(Boolean);

            if (validSubQuestions.length > 0) {
              loadedSubQuestionsForEdit = validSubQuestions.map(
                (item) => item.question
              );

              loadedSubQuestionEditorStates = validSubQuestions.map(
                (item) => item.questionEditorState
              );

              loadedSubQuestionAnswerEditorStates = validSubQuestions.map(
                (item) => item.answerEditorState
              );
            }
          }
        }

        const passage = question.passage_id
          ? meta.passages.find(
            (item) => String(item.passage_id) === String(question.passage_id)
          )
          : null;

        setForm((prev) => ({
          ...prev,

          selectedTitle: question.title || "",
          selectedType: question.qtype || null,
          selectedAnswerType: question.answer_type_id || null,
          selectedCategory: question.category_id || null,
          selectedDifficulty: question.difficulty_id || null,
          selectedGrade: question.grade_id || null,
          selectedSubject: question.subject_id || null,
          selectedLanguage: question.language_id || null,
          selectedTerm: question.term_id || null,
          selectedChapter: question.chapter_id || null,
          selectedYear: question.year_id || null,
          selectedSource: question.source_id || null,
          selectedAuthor: question.author_id || null,
          selectedPrivilege: question.privilege_id || null,
          selectedTag: question.tags || "",

          selectedIsPassage: Boolean(question.is_passage),
          selectedPassage: question.passage_id || null,
          selectedPassageType: passage?.passage_type_id || null,

          contentEditorState: rawToEditorState(questionRawList[0]),
          guidanceEditorState: rawToEditorState(guidanceRawList[0]),

          choicesEditorState: isMCQ
            ? questionRawList.slice(1).map(rawToEditorState)
            : prev.choicesEditorState,

          selectedChoices: isMCQ
            ? String(question.answer || "")
              .split(",")
              .filter(Boolean)
              .map(Number)
            : prev.selectedChoices,

          descriptiveAnswerEditorState:
            isDescriptive
              ? rawToEditorState(answerRawList[0])
              : prev.descriptiveAnswerEditorState,

          subQuestionEditorStates: isSubQuestion
            ? loadedSubQuestionEditorStates
            : prev.subQuestionEditorStates,

          subQuestionAnswerEditorStates: isSubQuestion
            ? loadedSubQuestionAnswerEditorStates
            : prev.subQuestionAnswerEditorStates,

          loadedSubQuestions: isSubQuestion
            ? loadedSubQuestionsForEdit
            : prev.loadedSubQuestions,

          removedSubQuestionIds: isSubQuestion
            ? []
            : prev.removedSubQuestionIds,


          submissionAnswerKey: isSubmission
            ? question.answer
            : prev.submissionAnswerKey,

          submissionAnswerContent: isSubmission
            ? parseSubmissionAnswer(answerContent)
            : prev.submissionAnswerContent,

          submissionFiles: isSubmission
            ? []
            : prev.submissionFiles,

          selectedSection: question.section_id || null,
        }));

        if (question.passage_id) {
          await loadSelectedPassage(question.passage_id);
        }
      } catch (error) {
        setEditError(error?.message || "Failed to load question");
      } finally {
        setEditLoading(false);
      }
    },
    [loadSelectedPassage, meta.passages]
  );

  useEffect(() => {
    if (!editQuestionProp) return;
    if (!meta.types.length) return;

    loadQuestionForEdit(editQuestionProp);
  }, [editQuestionProp, meta.types.length, loadQuestionForEdit]);

  useEffect(() => {
    if (!editQuestionId) return;
    if (!meta.types.length) return;

    let mounted = true;

    async function loadEditQuestionById() {
      try {
        setEditLoading(true);
        setEditError("");

        const result = await questionEditorService.getQuestionEditDetail(editQuestionId);

        if (!mounted) return;

        if (!result?.success) {
          throw new Error(result?.message || "Failed to load question");
        }

        const question = result.data?.question;

        if (!question) {
          throw new Error("Question not found");
        }

        await loadQuestionForEdit(question);
      } catch (error) {
        if (mounted) {
          setEditError(error?.message || "Failed to load question");
        }
      } finally {
        if (mounted) {
          setEditLoading(false);
        }
      }
    }

    loadEditQuestionById();

    return () => {
      mounted = false;
    };
  }, [editQuestionId, meta.types.length, loadQuestionForEdit]);

  const buildSubQuestionCreatePayload = useCallback(
    (index) => {
      const subQuestion = {
        title: form.selectedTitle,
        qType: form.selectedType,
        answer_type_id: null,
        categoryId: form.selectedCategory || null,
        questionKey: `${Date.now()}-sub-edit-${index}`,
        questionContent: [
          convertToRaw(form.subQuestionEditorStates[index].getCurrentContent()),
        ],
        createdBy: DataHandler.getFromSession("user_id"),

        answerKey: `${Date.now()}-answer-edit-${index}`,
        answerContent: [
          convertToRaw(
            form.subQuestionAnswerEditorStates[index].getCurrentContent()
          ),
        ],

        difficulty_id: form.selectedDifficulty || null,
        language_id: form.selectedLanguage || null,
        source_id: form.selectedSource || null,
        subject_id: form.selectedSubject || null,
        chapter_id: form.selectedChapter || null,
        grade_id: form.selectedGrade ? parseInt(form.selectedGrade, 10) : null,
        guidence: [convertToRaw(form.guidanceEditorState.getCurrentContent())],
        author_id: form.selectedAuthor || null,
        year_id: form.selectedYear || null,
        term_id: form.selectedTerm || null,
        privilege_id: form.selectedPrivilege || null,
        tags: form.selectedTag,
        assessment_id: form.selectedAssessmentId || null,
      };

      if (form.selectedAssessmentId) {
        subQuestion.section_id = form.selectedSection
          ? parseInt(form.selectedSection, 10)
          : null;
      }

      return subQuestion;
    },
    [form]
  );

  const updateQuestion = useCallback(async () => {
    if (!editQuestion) {
      throw new Error("No question selected for editing");
    }

    const isMCQ = editQuestion.qtype === questionTypeConstants.MCQ.id;
    const isDescriptive =
      editQuestion.qtype === questionTypeConstants.DESCRIPTIVE.id;
    const isSubQuestion =
      editQuestion.qtype === questionTypeConstants.SUBQUESTION.id;
    const isSubmission =
      editQuestion.qtype === questionTypeConstants.SUBMISSION.id;

    setSaving(true);

    try {
      let currentSubQuestionIds = (form.loadedSubQuestions || [])
        .map((item) => item.question_id)
        .filter(Boolean);

      if (isSubQuestion) {
        const existingSubQuestions = form.loadedSubQuestions || [];

        for (let i = 0; i < existingSubQuestions.length; i++) {
          const subQuestion = existingSubQuestions[i];

          const subPayload = {
            question_id: subQuestion.question_id,
            answer_type_id: null,
            title: form.selectedTitle,
            subject_id: form.selectedSubject,
            source_id: form.selectedSource,
            author_id: form.selectedAuthor,
            year_id: form.selectedYear,
            term_id: form.selectedTerm,
            tags: form.selectedTag,
            qtype: subQuestion.qtype,
            category_id: form.selectedCategory,
            chapter_id: form.selectedChapter,
            grade_id: form.selectedGrade,
            guidence: [convertToRaw(form.guidanceEditorState.getCurrentContent())],

            question_key: subQuestion.content,
            question_content: [
              convertToRaw(form.subQuestionEditorStates[i].getCurrentContent()),
            ],
            created_by: subQuestion.created_by,

            answer_key: subQuestion.answer,
            answer: subQuestion.answer,
            answer_content: [
              convertToRaw(
                form.subQuestionAnswerEditorStates[i].getCurrentContent()
              ),
            ],

            difficulty_id: form.selectedDifficulty,
            privilege_id: form.selectedPrivilege,
            language_id: form.selectedLanguage,

            is_content_blob: subQuestion.is_content_blob,
            is_answer_blob: subQuestion.is_answer_blob,

            is_passage: false,
            passage_id: null,
            is_subquestion: "true",
            sub_questions: null,

            section_id: form.selectedSection
              ? parseInt(form.selectedSection, 10)
              : null,
          };

          const subResult = await libraryService.updateQuestion(subPayload);

          if (!subResult?.success) {
            throw new Error(
              subResult?.message || `Failed to update sub question ${i + 1}`
            );
          }
        }

        for (
          let i = existingSubQuestions.length;
          i < form.subQuestionEditorStates.length;
          i++
        ) {
          const createPayload = buildSubQuestionCreatePayload(i);

          const createResult = form.selectedAssessmentId
            ? await assessmentQuestionService.saveSubQuestion(createPayload)
            : await descriptiveService.saveSubQuestion(createPayload);

          if (!createResult?.success) {
            throw new Error(
              createResult?.message || `Failed to create sub question ${i + 1}`
            );
          }

          const newQuestionId = createResult.data?.question_id;

          if (newQuestionId) {
            currentSubQuestionIds.push(newQuestionId);
          }
        }

        const removedIds = form.removedSubQuestionIds || [];

        for (const removedId of removedIds) {
          await libraryService.deleteQuestion({
            question_id: removedId,
            qtype: questionTypeConstants.SUBQUESTION.id,
            is_subquestion: "true",
          });
        }
      }

      const questionContent = [
        convertToRaw(form.contentEditorState.getCurrentContent()),
      ];

      let answer = editQuestion.answer;
      let answerContent;

      if (isMCQ) {
        questionContent.push(
          ...form.choicesEditorState.map((editorState) =>
            convertToRaw(editorState.getCurrentContent())
          )
        );

        answer = form.selectedChoices.toString();
      }

      if (isDescriptive) {
        answerContent = [
          convertToRaw(form.descriptiveAnswerEditorState.getCurrentContent()),
      ];
    }

    if (isSubmission) {
      answerContent = form.submissionAnswerContent;
    }

    if (isSubQuestion) {
      answer = "0";
      answerContent = "0";
    }

    const payload = {
      question_id: editQuestion.question_id,
      answer_type_id: form.selectedAnswerType,
      title: form.selectedTitle,
      subject_id: form.selectedSubject,
      source_id: form.selectedSource,
      author_id: form.selectedAuthor,
      year_id: form.selectedYear,
      term_id: form.selectedTerm,
      tags: form.selectedTag,
      qtype: editQuestion.qtype,
      category_id: form.selectedCategory,
      chapter_id: form.selectedChapter,
      grade_id: form.selectedGrade,
      guidence: [convertToRaw(form.guidanceEditorState.getCurrentContent())],

      question_key: editQuestion.content,
      question_content: questionContent,
      created_by: editQuestion.created_by,

      answer_key: editQuestion.answer,
      answer,
      answer_content: answerContent,

      difficulty_id: form.selectedDifficulty,
      privilege_id: form.selectedPrivilege,
      language_id: form.selectedLanguage,

      is_content_blob: editQuestion.is_content_blob,
      is_answer_blob: editQuestion.is_answer_blob,

      is_passage: form.selectedIsPassage,
      passage_id: form.selectedIsPassage ? form.selectedPassage : null,

      is_subquestion: editQuestion.is_subquestion,

      sub_questions: isSubQuestion
        ? currentSubQuestionIds.filter(Boolean).join(",")
        : editQuestion.sub_questions,

      section_id: form.selectedSection ? parseInt(form.selectedSection, 10) : null,
    };

    const result = await libraryService.updateQuestion(payload);

    if (!result?.success) {
      throw new Error(result?.message || "Failed to update question");
    }

    setForm((prev) => ({
      ...prev,
      loadedSubQuestions: currentSubQuestionIds.map((question_id, index) => ({
        ...(prev.loadedSubQuestions?.[index] || {}),
        question_id,
      })),
      removedSubQuestionIds: [],
    }));

    return result;
  } finally {
    setSaving(false);
  }
}, [editQuestion, form, buildSubQuestionCreatePayload]);

  const refreshPassages = useCallback(async () => {
    const result = await passageService.getPassageTitle();

    if (!result?.success) {
      throw new Error(result?.message || "Failed to refresh passages");
    }

    setMeta((prev) => ({
      ...prev,
      passages: result.data?.passages || [],
    }));

    return result.data?.passages || [];
  }, []);

  const createPassage = useCallback(async (passage) => {
    const result = await passageService.addPassage(passage);

    if (!result?.success) {
      throw new Error(result?.message || "Failed to create passage");
    }

    await refreshPassages();

    updateField("selectedPassage", result.data?.passage_id || "");
    await loadSelectedPassage(result.data?.passage_id);

    return result;
  }, [refreshPassages, updateField, loadSelectedPassage]);

  const editPassage = useCallback(async (passageId, passage) => {
    const result = await passageService.updatePassage(passage, passageId);

    if (!result?.success) {
      throw new Error(result?.message || "Failed to update passage");
    }

    await refreshPassages();
    await loadSelectedPassage(passageId);

    return result;
  }, [refreshPassages, loadSelectedPassage]);

  const addSubQuestion = useCallback(() => {
    setForm((prev) => ({
      ...prev,
      subQuestionEditorStates: [
        ...prev.subQuestionEditorStates,
        EditorState.createEmpty(),
      ],
      subQuestionAnswerEditorStates: [
        ...prev.subQuestionAnswerEditorStates,
        EditorState.createEmpty(),
      ],
    }));
  }, []);

  const removeSubQuestion = useCallback((index) => {
    setForm((prev) => {
      if (prev.subQuestionEditorStates.length <= 1) return prev;

      const removedSubQuestion = prev.loadedSubQuestions?.[index];

      return {
        ...prev,

        subQuestionEditorStates: prev.subQuestionEditorStates.filter(
          (_, i) => i !== index
        ),

        subQuestionAnswerEditorStates: prev.subQuestionAnswerEditorStates.filter(
          (_, i) => i !== index
        ),

        loadedSubQuestions: (prev.loadedSubQuestions || []).filter(
          (_, i) => i !== index
        ),

        removedSubQuestionIds: removedSubQuestion?.question_id
          ? [
            ...(prev.removedSubQuestionIds || []),
            removedSubQuestion.question_id,
          ]
          : prev.removedSubQuestionIds || [],
      };
    });
  }, []);

  const updateSubQuestionEditor = useCallback((index, editorState) => {
    setForm((prev) => {
      const next = [...prev.subQuestionEditorStates];
      next[index] = editorState;

      return {
        ...prev,
        subQuestionEditorStates: next,
      };
    });
  }, []);

  const updateSubQuestionAnswerEditor = useCallback((index, editorState) => {
    setForm((prev) => {
      const next = [...prev.subQuestionAnswerEditorStates];
      next[index] = editorState;

      return {
        ...prev,
        subQuestionAnswerEditorStates: next,
      };
    });
  }, []);

  const saveSubQuestionBundle = useCallback(async () => {
    setSaving(true);

    try {
      const subQuestionIds = [];

      for (let i = 0; i < form.subQuestionEditorStates.length; i++) {
        const subQuestion = {
          title: form.selectedTitle,
          qType: form.selectedType,
          answer_type_id: null,
          categoryId: form.selectedCategory || null,
          questionKey: `${Date.now()}-sub-${i}`,
          questionContent: [
            convertToRaw(form.subQuestionEditorStates[i].getCurrentContent()),
          ],
          createdBy: DataHandler.getFromSession("user_id"),

          answerKey: `${Date.now()}-answer-${i}`,
          answerContent: [
            convertToRaw(
              form.subQuestionAnswerEditorStates[i].getCurrentContent()
            ),
          ],

          difficulty_id: form.selectedDifficulty || null,
          language_id: form.selectedLanguage || null,
          source_id: form.selectedSource || null,
          subject_id: form.selectedSubject || null,
          chapter_id: form.selectedChapter || null,
          grade_id: form.selectedGrade ? parseInt(form.selectedGrade, 10) : null,
          guidence: [convertToRaw(form.guidanceEditorState.getCurrentContent())],
          author_id: form.selectedAuthor || null,
          year_id: form.selectedYear || null,
          term_id: form.selectedTerm || null,
          privilege_id: form.selectedPrivilege || null,
          tags: form.selectedTag,
          assessment_id: form.selectedAssessmentId || null,
          //section_id: form.selectedSection ? parseInt(form.selectedSection, 10) : null,
        };

        if (form.selectedAssessmentId) {
          subQuestion.section_id = form.selectedSection
            ? parseInt(form.selectedSection, 10)
            : null;
        }

        const result = form.selectedAssessmentId
          ? await assessmentQuestionService.saveSubQuestion(subQuestion)
          : await descriptiveService.saveSubQuestion(subQuestion);

        if (!result?.success) {
          throw new Error(result?.message || "Failed to create sub question");
        }

        subQuestionIds.push(result.data?.question_id);
      }

      const mainQuestion = {
        title: form.selectedTitle,
        qType: form.selectedType,
        answer_type_id: null,
        categoryId: form.selectedCategory || null,
        questionKey: `${Date.now()}-main`,
        questionContent: [
          convertToRaw(form.contentEditorState.getCurrentContent()),
        ],
        createdBy: DataHandler.getFromSession("user_id"),

        answerKey: `${Date.now()}-main-answer`,
        answerContent: "0",
        sub_questions: subQuestionIds.filter(Boolean).join(","),

        difficulty_id: form.selectedDifficulty || null,
        language_id: form.selectedLanguage || null,
        source_id: form.selectedSource || null,
        subject_id: form.selectedSubject || null,
        chapter_id: form.selectedChapter || null,
        grade_id: form.selectedGrade ? parseInt(form.selectedGrade, 10) : null,
        guidence: [convertToRaw(form.guidanceEditorState.getCurrentContent())],
        author_id: form.selectedAuthor || null,
        year_id: form.selectedYear || null,
        term_id: form.selectedTerm || null,
        privilege_id: form.selectedPrivilege || null,
        tags: form.selectedTag,

        is_passage: Boolean(form.selectedIsPassage),
        passage_id:
          form.selectedIsPassage && form.selectedPassage
            ? parseInt(form.selectedPassage, 10)
            : null,
        // section_id: null,

        assessment_id: form.selectedAssessmentId || null,
        // section_id: form.selectedSection ?parseInt(form.selectedSection, 10) : null,
      };

      if (form.selectedAssessmentId) {
        mainQuestion.section_id = form.selectedSection
          ? parseInt(form.selectedSection, 10)
          : null;
      }

      const mainResult = form.selectedAssessmentId
        ? await assessmentQuestionService.saveMainQuestion(mainQuestion)
        : await descriptiveService.saveMainQuestion(mainQuestion);

      if (!mainResult?.success) {
        throw new Error(mainResult?.message || "Failed to create main question");
      }

      //resetForm();
      return mainResult;
    } finally {
      setSaving(false);
    }
  }, [form, resetForm]);

  const uploadSubmission = useCallback(async (files) => {
    setForm((prev) => ({
      ...prev,
      submissionUploaded: false,
    }));

    const answerKey = `${Date.now()}`;
    const uniqueContainer = config.storageBlobConfigs.containerName;
    const submissionNames = [];

    try {
      for (const file of files) {
        const cleanName = file.name.replace(/['"]+/g, "");
        submissionNames.push(cleanName);

        const fileBuffer = await new Promise((resolve, reject) => {
          const reader = new FileReader();

          reader.onload = (event) => resolve(event.target.result);
          reader.onerror = reject;

          reader.readAsArrayBuffer(file);
        });

        await AzureStorage.uploadBlob(uniqueContainer, cleanName, fileBuffer);
      }

      setForm((prev) => ({
        ...prev,
        submissionAnswerKey: answerKey,
        submissionAnswerContent: [
          ...prev.submissionAnswerContent,
          ...submissionNames,
        ],
        submissionUploaded: true,
      }));

      if (window.Swal) {
        window.Swal.fire({
          title: "Success!",
          text: "File uploaded successfully",
          icon: "success",
        });
      }

      return {
        answerKey,
        answerContent: submissionNames,
      };
    } catch (error) {
      setForm((prev) => ({
        ...prev,
        submissionUploaded: true,
      }));

      throw error;
    }
  }, []);


  const getSubmissionContent = useCallback(
    async (file) => {
      const nextFiles = [...form.submissionFiles, file];

      setForm((prev) => ({
        ...prev,
        submissionFiles: nextFiles,
        submissionClearFile: false,
      }));

      return uploadSubmission(nextFiles);
    },
    [form.submissionFiles, uploadSubmission]
  );

  const deleteSubmissionContent = useCallback(async (file) => {
    const fileName = String(file?.name || file)
      .replace(/['"]+/g, "")
      .trim();

    setForm((prev) => {
      if (prev.submissionClearFile) return prev;

      return {
        ...prev,
        submissionFiles: prev.submissionFiles.filter(
          (item) => String(item?.name || item) !== fileName
        ),
        submissionAnswerContent: prev.submissionAnswerContent.filter(
          (name) => String(name).replace(/['"]+/g, "").trim() !== fileName
        ),
      };
    });

    const uniqueContainer = config.storageBlobConfigs.containerName;
    await AzureStorage.deleteblob(uniqueContainer, fileName);
  }, []);

  const saveSubmissionQuestion = useCallback(async () => {
    const questionKey = `${Date.now()}`;
    let answerKey = form.submissionAnswerKey;
    let answerContent = form.submissionAnswerContent;

    if (!answerKey || answerContent.length === 0) {
      const uploaded = await uploadSubmission(form.submissionFiles);
      answerKey = uploaded.answerKey;
      answerContent = uploaded.answerContent;
    }

    const question = {
      title: form.selectedTitle,
      qType: form.selectedType,
      answer_type_id: null,
      categoryId: form.selectedCategory || null,
      questionKey,
      questionContent: [
        convertToRaw(form.contentEditorState.getCurrentContent()),
      ],
      createdBy: DataHandler.getFromSession("user_id"),

      answerKey,
      answerContent,

      difficulty_id: form.selectedDifficulty || null,
      language_id: form.selectedLanguage || null,
      source_id: form.selectedSource || null,
      subject_id: form.selectedSubject || null,
      chapter_id: form.selectedChapter || null,
      grade_id: form.selectedGrade ? parseInt(form.selectedGrade, 10) : null,
      guidence: [convertToRaw(form.guidanceEditorState.getCurrentContent())],

      author_id: form.selectedAuthor || null,
      year_id: form.selectedYear || null,
      privilege_id: form.selectedPrivilege || null,
      term_id: form.selectedTerm || null,
      tags: form.selectedTag,

      is_passage: form.selectedIsPassage,
      passage_id: form.selectedIsPassage ? form.selectedPassage : null,

      assessment_id: form.selectedAssessmentId || null,
      // section_id: form.selectedSection
      //   ? parseInt(form.selectedSection, 10)
      //   : null,
    };

    if (form.selectedAssessmentId) {
      question.section_id = form.selectedSection
        ? parseInt(form.selectedSection, 10)
        : null;

    }

    setSaving(true);

    try {
      const result = form.selectedAssessmentId
        ? await assessmentQuestionService.saveQuestion(question)
        : await submissionService.saveQuestion(question);

      if (!result?.success) {
        throw new Error(result?.message || "Failed to create Submission question");
      }

      // setForm((prev) => ({
      //   ...initialForm,
      //   selectedType: prev.selectedType,
      //   selectedAnswerType: prev.selectedAnswerType,
      //   selectedPrivilege: prev.selectedPrivilege,
      //   submissionClearFile: true,
      // }));

      return result;
    } finally {
      setSaving(false);
    }
  }, [form, uploadSubmission]);

  const value = useMemo(
    () => ({
      form,
      meta,
      metaLoading,
      metaError,
      saving,
      setSaving,
      editQuestion,
      editLoading,
      editError,
      updateField,
      addOption,
      removeOption,
      updateChoiceEditor,
      updateChoiceMarks,
      resetForm,
      saveMCQQuestion,
      saveDescriptiveQuestion,
      loadQuestionForEdit,
      updateQuestion,
      selectedPassageDetails,
      selectedPassageLoading,
      selectedPassageError,
      loadSelectedPassage,
      createPassage,
      editPassage,
      addSubQuestion,
      removeSubQuestion,
      updateSubQuestionEditor,
      updateSubQuestionAnswerEditor,
      saveSubQuestionBundle,
      getSubmissionContent,
      deleteSubmissionContent,
      saveSubmissionQuestion,
      createQuestionWithAssessment,
    }),
    [
      form,
      meta,
      metaLoading,
      metaError,
      saving,
      editQuestion,
      editLoading,
      editError,
      updateField,
      addOption,
      removeOption,
      updateChoiceEditor,
      updateChoiceMarks,
      resetForm,
      saveMCQQuestion,
      saveDescriptiveQuestion,
      loadQuestionForEdit,
      updateQuestion,
      selectedPassageDetails,
      selectedPassageLoading,
      selectedPassageError,
      loadSelectedPassage,
      createPassage,
      editPassage,
      addSubQuestion,
      removeSubQuestion,
      updateSubQuestionEditor,
      updateSubQuestionAnswerEditor,
      saveSubQuestionBundle,
      getSubmissionContent,
      deleteSubmissionContent,
      saveSubmissionQuestion,
      createQuestionWithAssessment,
    ]
  );

  return (
    <QuestionEditorContext.Provider value={value}>
      {children}
    </QuestionEditorContext.Provider>
  );
}

export function useQuestionEditor() {
  const context = useContext(QuestionEditorContext);

  if (!context) {
    throw new Error(
      "useQuestionEditor must be used inside QuestionEditorProvider"
    );
  }

  return context;
}
